import { MoreHorizontal } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export type RowAction = {
  readonly id: string;
  readonly label: string;
  readonly onSelect: () => void;
  readonly icon?: LucideIcon;
  readonly tone?: 'default' | 'destructive';
  /**
   * ONLY for a write that is already in flight on this row. The standard is explicit that the menu
   * carries "only the actions valid in the current state … never disabled": an action that does not
   * apply is ABSENT, decided by the caller building this array. A greyed-out item here means "this
   * one is running", never "you may not".
   */
  readonly busy?: boolean;
};

/**
 * The list row's `···` menu (§ List: "'···' carrying only the actions valid in the current state").
 *
 * WHY A DISCLOSURE AND NOT `role="menu"`. Exactly the reasoning `AccountMenu` records for the rail's
 * account panel, and kept identical so the platform has one popover behaviour rather than two: the
 * `menu`/`menuitem` roles bring an arrow-key roving-focus model, type-ahead and a focus trap that a
 * person then expects to work like a desktop menu bar, and half-implementing that is worse than not
 * claiming it. A button with `aria-expanded` controlling a small group of buttons is the WAI-ARIA
 * Disclosure pattern, and Tab walks it.
 *
 * WHY THE PANEL IS PORTALLED — this is the part that is not a preference. `Table` wraps every table
 * in an `overflow-x: auto` container so wide tables scroll rather than pushing the page sideways.
 * An absolutely-positioned panel inside a row is clipped by that container: on a table with any
 * horizontal overflow the menu opens and is simply not visible, and on one without it the menu is
 * cut off at the table's bottom edge. Portalling to `document.body` — which is OUTSIDE `.app-shell`,
 * so `position: fixed` there means the viewport rather than the layout-contained shell box —
 * escapes both.
 *
 * The trade the portal makes is that the panel no longer moves with the page, so it CLOSES on any
 * scroll or resize rather than drifting away from its own row.
 *
 * It starts LISTENING for scrolls one frame after it opens. A browser delivers a scroll event at
 * the next rendering update, not when the scroll happens, so a trigger scrolled into view just
 * before the press — by focus, by a keyboard, by an automated click — would otherwise have its own
 * scroll arrive after the panel opened and close it on the spot. Scroll events are dispatched
 * before animation-frame callbacks in the same update, so a listener attached from one hears only
 * the scrolls that begin after the panel is open.
 */
/** A menu item's height, for deciding before it opens whether the panel fits below. */
const ITEM_HEIGHT = 44;

/** Closes the open menu on an outside press, Escape, a scroll or a resize. */
function useCloseMenu(
  open: boolean,
  setPosition: (position: null) => void,
  panelRef: RefObject<HTMLDivElement | null>,
  triggerRef: RefObject<HTMLButtonElement | null>,
) {
  useEffect(() => {
    if (!open) {
      return;
    }

    function close() {
      setPosition(null);
    }

    function handlePointerDown(event: PointerEvent) {
      if (!(event.target instanceof Node)) {
        return;
      }
      // The trigger is excluded too: without this, clicking it while open would close via this
      // listener and immediately reopen via the click handler.
      if (
        panelRef.current?.contains(event.target) === true ||
        triggerRef.current?.contains(event.target) === true
      ) {
        return;
      }
      close();
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    // `capture: true` so a scroll inside the table's own overflow container is seen too — a
    // non-capturing window listener never hears it, and the panel would hang in mid-air. Attached
    // from the next frame — see the component's comment.
    const frame = requestAnimationFrame(() => {
      window.addEventListener('scroll', close, true);
      window.addEventListener('resize', close);
    });
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, setPosition, panelRef, triggerRef]);
}

export function RowActionsMenu({
  actions,
  label,
  triggerClassName,
}: {
  readonly actions: readonly RowAction[];
  /** The accessible name of the trigger — name the ROW, e.g. "More actions for Acme Srl". */
  readonly label: string;
  /** Extra classes on the trigger, e.g. `size-11` for a 44 px touch target on a phone's card (U16). */
  readonly triggerClassName?: string;
}) {
  const { t } = useTranslation('common');
  // `top` when the menu hangs below its trigger, `bottom` when there is no room below and it opens
  // above it instead: a row near the bottom of the screen must not open its menu off screen.
  const [position, setPosition] = useState<
    { top: number; right: number } | { bottom: number; right: number } | null
  >(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();
  const open = position !== null;

  useCloseMenu(open, setPosition, panelRef, triggerRef);

  // A row with nothing valid to offer shows no control at all, rather than an empty menu or a
  // disabled button — the standard's "actions the caller is not permitted do not appear".
  if (actions.length === 0) {
    return null;
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={cn(
          `row-menu-trigger text-muted-foreground hover:text-foreground focus-visible:ring-ring
          inline-flex size-8 items-center justify-center rounded-md focus-visible:ring-2
          focus-visible:outline-none`,
          triggerClassName,
        )}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={label}
        title={t('rowActions')}
        onClick={() => {
          if (open) {
            setPosition(null);
            return;
          }
          const rect = triggerRef.current?.getBoundingClientRect();
          if (rect === undefined) {
            return;
          }
          // Right-anchored, in the same right-origin coordinates the panel's own `right` style
          // uses, so the menu hangs under the trigger's right edge and never off the page.
          const right = Math.max(8, window.innerWidth - rect.right);
          // The panel's height before it exists: one item row each, plus the panel's padding.
          const needed = actions.length * ITEM_HEIGHT + 8 + 4;
          const roomBelow = window.innerHeight - rect.bottom;
          setPosition(
            roomBelow >= needed || roomBelow >= rect.top
              ? { top: rect.bottom + 4, right }
              : { bottom: window.innerHeight - rect.top + 4, right },
          );
        }}
      >
        <MoreHorizontal aria-hidden="true" className="size-4" />
      </button>
      {position === null
        ? null
        : createPortal(
            <div ref={panelRef} id={panelId} className="row-menu-panel" style={position}>
              {actions.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  className="row-menu-item"
                  data-tone={action.tone ?? 'default'}
                  disabled={action.busy === true}
                  onClick={() => {
                    setPosition(null);
                    action.onSelect();
                  }}
                >
                  {action.icon === undefined ? null : (
                    <action.icon aria-hidden="true" className="size-4 shrink-0" />
                  )}
                  {action.label}
                </button>
              ))}
            </div>,
            document.body,
          )}
    </>
  );
}
